// ABOUTME: Displays tasks whose category is produced by another (virtual) shop.
// ABOUTME: Collapsible section listing the products and the shop that bakes them.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, LocalShipping as LocalShippingIcon } from '@mui/icons-material';
import SortableHeaderCell from './SortableHeaderCell';
import useSortableTable from '../../hooks/useSortableTable';
import { colors } from '../../theme/colors';

const GRID_COLUMNS = '3fr 1.5fr 1fr 0.7fr 0.7fr 0.7fr 1.5fr';

function ProducedElsewhereSection({ tasks, expanded, onExpandChange, onProductSelect, testModeEnabled = false }) {
  const { t } = useTranslation();

  const { sortedItems, sortKey, sortDirection, handleSort, isSortable } = useSortableTable(tasks, {
    defaultSortKey: 'urgency_score',
    defaultSortDirection: 'desc',
  });

  if (!tasks || tasks.length === 0) {
    return null;
  }

  const TableHeader = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        py: 1,
        px: 2,
        bgcolor: colors.background.light,
        fontSize: '0.75rem',
        fontWeight: 600,
        color: colors.text.secondary,
        borderTop: `1px solid ${colors.border.light}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SortableHeaderCell
          sortKey="product_id"
          currentSortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSortable={isSortable('product_id')}
        >
          {t('dashboard.schedule.table.id')}
        </SortableHeaderCell>
        <Box>/</Box>
        <SortableHeaderCell
          sortKey="product_name"
          currentSortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          isSortable={isSortable('product_name')}
        >
          {t('dashboard.schedule.table.product')}
        </SortableHeaderCell>
      </Box>
      <SortableHeaderCell
        sortKey="category.name"
        currentSortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        isSortable={isSortable('category.name')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.schedule.table.category')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="quantity"
        currentSortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        isSortable={isSortable('quantity')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.schedule.table.quantity')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="current_stock"
        currentSortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        isSortable={isSortable('current_stock')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.schedule.table.stock')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="expected_demand"
        currentSortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        isSortable={isSortable('expected_demand')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.recommendations.demand')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="urgency_score"
        currentSortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        isSortable={isSortable('urgency_score')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.schedule.table.priority')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="produced_by_shop_name"
        currentSortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        isSortable={isSortable('produced_by_shop_name')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.schedule.producedElsewhere.producedBy')}
      </SortableHeaderCell>
    </Box>
  );

  const renderRow = (task, index) => {
    const categoryColor = task.category?.color || colors.category.default;

    const handleClick = () => {
      if (onProductSelect && task.product_id) {
        onProductSelect(task.product_id);
      }
    };

    return (
      <Box
        key={`produced-elsewhere-${task.product_id}-${index}`}
        onClick={handleClick}
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID_COLUMNS,
          alignItems: 'center',
          py: 1.5,
          px: 2,
          borderBottom: `1px solid ${colors.border.lighter}`,
          cursor: onProductSelect ? 'pointer' : 'default',
          '&:hover': {
            bgcolor: colors.background.hover,
          },
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
      >
        {/* Product Name */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minHeight: '40px',
          }}
        >
          {/* Category color indicator */}
          <Box
            sx={{
              width: '6px',
              height: '32px',
              borderRadius: '3px',
              bgcolor: categoryColor,
              flexShrink: 0,
              ml: -0.5,
            }}
          />
          {task.product_id && (
            <Box
              sx={{
                fontSize: '0.75rem',
                color: colors.text.muted,
                flexShrink: 0,
              }}
            >
              #{task.product_id}
            </Box>
          )}
          <Box
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: '0.875rem',
              lineHeight: 1.3,
            }}
          >
            {task.product_name}
          </Box>
        </Box>

        {/* Category */}
        <Box sx={{ textAlign: 'center', fontSize: '0.875rem', color: colors.text.secondary }}>
          {task.category?.name || '-'}
        </Box>

        {/* Quantity */}
        <Box sx={{ color: colors.text.primary, fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Box component="span">
            <Box component="span" sx={{ fontWeight: 700 }}>{task.quantity ?? '-'}</Box> <Box component="span" sx={{ fontWeight: 700 }}>{t('common.unitsShort')}</Box>
          </Box>
          {testModeEnabled && (
            <>
              <Box component="span" sx={{ color: colors.text.muted }}>|</Box>
              <Box component="span">
                {task.batches ?? '-'} {t('common.batchesShort')}
              </Box>
            </>
          )}
        </Box>

        {/* Stock */}
        <Box sx={{ color: task.current_stock < 0 ? colors.status.error : colors.text.primary, fontSize: '0.875rem', textAlign: 'center' }}>
          {task.current_stock != null ? task.current_stock.toFixed(0) : '-'}
        </Box>

        {/* Demand */}
        <Box sx={{ color: colors.text.primary, fontSize: '0.875rem', textAlign: 'center' }}>
          {task.expected_demand != null ? task.expected_demand.toFixed(0) : '-'}
        </Box>

        {/* Priority */}
        <Box sx={{ color: colors.text.primary, fontSize: '0.875rem', textAlign: 'center' }}>
          {task.urgency_score?.toFixed(0) ?? '-'}
        </Box>

        {/* Producing shop */}
        <Box sx={{ color: colors.text.primary, fontSize: '0.875rem', textAlign: 'center', fontWeight: 600 }}>
          {task.produced_by_shop_name || '-'}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* Section Title */}
      <Box
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: colors.schedule.stockDark,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {t('dashboard.schedule.producedElsewhere.title')}
      </Box>

      <Accordion
        expanded={expanded}
        onChange={(e, isExpanded) => onExpandChange(isExpanded)}
        sx={{
          bgcolor: colors.background.white,
          borderRadius: '12px !important',
          border: `3px solid ${colors.schedule.stock}`,
          overflow: 'hidden',
          '&:before': { display: 'none' },
          boxShadow: 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: colors.schedule.stock,
            color: 'white',
            minHeight: '48px',
            '&.Mui-expanded': {
              minHeight: '48px',
            },
            '& .MuiAccordionSummary-content': {
              my: 1,
              '&.Mui-expanded': {
                my: 1,
              },
            },
            '& .MuiSvgIcon-root': { color: 'white' },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <LocalShippingIcon fontSize="small" />
            <Box sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
              {tasks.length} {t('dashboard.schedule.producedElsewhere.title').toLowerCase()}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableHeader />
          {sortedItems.map((task, index) => renderRow(task, index))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default ProducedElsewhereSection;
